import { isEqual } from 'lodash';
import { QueryModelBase, queryCreateFromRequestProps } from '@goodt-common/data';
import { resolveAddedAndDeleted } from '../../mixins/utils';

import { EntityType } from '../const';
import { QueryKey } from '../../utils/query-const';

/**
 * @typedef {import('../const').EntityTypeEnum} EntityTypeEnum
 * @typedef {import('@goodt-common/data').IQueryFilterInfo} IQueryFilterInfo
 * @typedef {import('@goodt-common/data').IQuerySortInfo} IQuerySortInfo
 * @typedef {{ name: string, type: EntityTypeEnum[keyof EntityTypeEnum] }} IDatasetQueryParamChange
 * @typedef {{ added: IDatasetQueryParamChange[], deleted: IDatasetQueryParamChange[] }} IDatasetQueryParamChanges
 * @typedef {{ added: IQueryFilterInfo[], deleted: IQueryFilterInfo[] }} IDatasetQueryFiltersChanges
 * @typedef {{ added: IQuerySortInfo[], deleted: IQuerySortInfo[] }} IDatasetQuerySortChanges
 * @typedef {{
 *   metrics: IDatasetQueryParamChanges | null,
 *   dimensions: IDatasetQueryParamChanges | null,
 *   variables: IDatasetQueryParamChanges | null,
 *   filters: IDatasetQueryFiltersChanges | null,
 *   sort: IDatasetQuerySortChanges | null
 * }} IDatasetQueryChanges
 */

export default {
    data: () => ({
        /**
         * @type {QueryModelBase}
         */
        queryModelInitial: null
    }),
    computed: {
        /**
         * @return {QueryModelBase}
         */
        queryModelChanged() {
            const { query } = this.datasetPropsMutable;
            return new QueryModelBase(query);
        },
        /**
         *
         * @return {{ query: IDatasetQueryChanges }}
         */
        datasetChanges() {
            const { variablesChanges, metricsChanges, dimensionsChanges, filtersChanges, sortChanges } = this;
            return {
                query: {
                    metrics: metricsChanges,
                    dimensions: dimensionsChanges,
                    variables: variablesChanges,
                    filters: filtersChanges,
                    sort: sortChanges
                }
            };
        },
        /**
         * @return {IDatasetQueryParamChanges|null}
         */
        variablesChanges() {
            const { variableNames: variableNamesInitial } = this.queryModelInitial;
            const { variableNames: variableNamesChanged } = this.queryModelChanged;
            if (isEqual(variableNamesInitial, variableNamesChanged)) {
                return null;
            }
            const [added, deleted] = resolveAddedAndDeleted(variableNamesChanged, variableNamesInitial);
            return {
                added: added.map((name) => ({ name: name.slice(1), type: EntityType.VARIABLE })),
                deleted: deleted.map((name) => ({ name: name.slice(1), type: EntityType.VARIABLE }))
            };
        },
        /**
         * @return {IDatasetQueryParamChanges|null}
         */
        metricsChanges() {
            const { metrics: metricsInitial } = this.queryModelInitial;
            const { metrics: metricsChanged } = this.queryModelChanged;
            if (isEqual(metricsInitial, metricsChanged)) {
                return null;
            }

            const [added, deleted] = resolveAddedAndDeleted(metricsChanged, metricsInitial);
            return {
                added: added.map((name) => ({ name, type: EntityType.METRIC })),
                deleted: deleted.map((name) => ({ name, type: EntityType.METRIC }))
            };
        },
        /**
         * @return {IDatasetQueryParamChanges|null}
         */
        dimensionsChanges() {
            const { dimensions: dimensionsInitial } = this.queryModelInitial;
            const { dimensions: dimensionsChanged } = this.queryModelChanged;
            if (isEqual(dimensionsInitial, dimensionsChanged)) {
                return null;
            }
            /**
             * @param {string[]} added
             * @param {string[]} deleted
             */
            const [added, deleted] = resolveAddedAndDeleted(dimensionsChanged, dimensionsInitial);
            return {
                added: added.map((name) => ({ name, type: EntityType.DIMENSION })),
                deleted: deleted.map((name) => ({ name, type: EntityType.DIMENSION }))
            };
        },
        /**
         * @return {IDatasetQueryFiltersChanges|null}
         */
        filtersChanges() {
            const { filters: filtersInitial } = this.queryModelInitial;
            const { filters: filtersChanged } = this.queryModelChanged;
            if (isEqual(filtersInitial, filtersChanged)) {
                return null;
            }
            /**
             * @param {IQueryFilterInfo[]} added
             * @param {IQueryFilterInfo[]} deleted
             */
            const [added, deleted] = resolveAddedAndDeleted(filtersChanged, filtersInitial);
            return {
                added: added,
                deleted: deleted
            };
        },
        /**
         * @return {IDatasetQuerySortChanges|null}
         */
        sortChanges() {
            const { sort: sortInitial } = this.queryModelInitial;
            const { sort: sortChanged } = this.queryModelChanged;
            if (isEqual(sortInitial, sortChanged)) {
                return null;
            }
            /**
             * @param {IQuerySortInfo[]} added
             * @param {IQuerySortInfo[]} deleted
             */
            const [added, deleted] = resolveAddedAndDeleted(sortChanged, sortInitial);
            return {
                added: added,
                deleted: deleted
            };
        }
    },
    created() {
        this.$watch(
            `datasetPropsMutable.query.${QueryKey.FROM}`,
            ($from) => {
                const { query: initialQuery } = this.dataset ?? {};
                // if dataset was re-selected reset 'queryModelInitial' to blank one
                let resolvedQuery = initialQuery?.[QueryKey.FROM] !== $from ? null : initialQuery;
                resolvedQuery = queryCreateFromRequestProps({ query: resolvedQuery });
                this.queryModelInitial = new QueryModelBase(resolvedQuery);
            },
            { immediate: true }
        );
    },
    methods: {
        /**
         *
         * @return {string[]}
         */
        resolveAffectedCollapses() {
            const collapses = [];
            const { metrics, dimensions, variables, filters, sort } = this.datasetChanges.query;
            if (metrics || dimensions) {
                collapses.push('"Метрики/измерения"');
            }
            if (variables != null) {
                collapses.push('"Переменные"');
            }
            if (filters != null) {
                collapses.push('"Фильтры"');
            }
            if (sort != null) {
                collapses.push('"Сортировка"');
            }
            return collapses;
        }
    }
};
